import { Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { formatNumber } from "@entrilia/shared/utilities/formatters";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import ExportWithFiltersButton from "../../../common/ExportWithFiltersButton";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import { FilterContextProvider } from "../../../common/filters/FilterContextProvider";
import FiltersPanel from "../../../common/filters/FiltersPanel";
import { getInitialFilterState } from "../../../common/filters/filterState";
import DocumentViewsGrid from "./DocumentViewsGrid";
import { filterDefinitions, getSearchDocumentViewsFiltersPayload } from "./documentViewsFilterDefinitions";
import { getSelectOptionsForDocumentViews } from "./documentViewsSelectOptionsProvider";

const exportDocumentViewsToExcel = withErrorHandling(adminApi.exportDocumentViewsToExcel);

const DocumentActivityPage = () => {
  usePageTitle("Document Activity");

  const { clientCode } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const filterId = useMemo(() => `${clientCode}_document_activity_filter_v1`, [clientCode]);

  const [filterState, setFilterState] = useState(() => getInitialFilterState(filterId, filterDefinitions, []));

  const filterPayload = useMemo(() => getSearchDocumentViewsFiltersPayload(filterState), [filterState]);

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource } = useGridDataSourceWithRange(
    adminApi.searchDocumentViews,
    filterPayload
  );

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (withFilters: boolean) => {
    setIsExporting(true);

    const filters = withFilters ? filterPayload : {};
    const [resp, error] = await exportDocumentViewsToExcel({ range: {}, ...filters });

    setIsExporting(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[DocumentActivityPage] exportDocumentViewsToExcel");
      return;
    }

    await downloadFileFromUrl(resp.downloadUrl);

    if (resp.isTruncated) {
      sendNotification(
        `Exported first ${formatNumber(resp.exportedRecordsCount)} records. Please use filters to reduce the dataset and export additional records.`
      );
    }
  };

  return (
    <>
      <GeneralPageHeader title="Document Activity" />
      <Stack spacing={2.5} sx={{ py: 2.5, px: 3, height: "100%" }}>
        <FilterContextProvider
          filterState={filterState}
          setFilterState={setFilterState}
          getSelectOptionsForFilter={getSelectOptionsForDocumentViews}
        >
          <FiltersPanel
            recordCount={totalRows ?? 0}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            onRefresh={refreshDataSource}
            withoutSearch
          >
            <ExportWithFiltersButton
              disabled={!totalRows || isLoading}
              loading={isExporting}
              onExportAllRecords={() => handleExport(false)}
              onExportFilteredRecords={() => handleExport(true)}
            />
          </FiltersPanel>
        </FilterContextProvider>

        <DocumentViewsGrid apiRef={gridApiRef} dataSource={dataSource} filterId={filterId} />
      </Stack>
    </>
  );
};

export default DocumentActivityPage;
